import React, { useEffect, useRef } from 'react';
import './Character.scss';
import { sizeCanvas, cssSize } from '../../utils/canvasSize';

// Canvas 内部基準解像度（既存レイヤーと揃える）
const TARGET_W = 1920;
const TARGET_H = 1024;
const TARGET_RATIO = TARGET_W / TARGET_H;

// 背景 sofa.png の実寸（bgPosition 用）
const BG_W = 1920;
const BG_H = 2560;

/**
 * 背景(sofa.png)の cover 描画と同じ変換を求める
 * これを使うと、ウィンドウ比率が変わっても背景（カウンター等）に対する
 * 位置と大きさが固定される。canvas はビューポート全面サイズ前提。
 */
const calcBgTransform = () => {
  const winW = window.innerWidth;
  const winH = window.innerHeight;
  const imgRatio = BG_W / BG_H;
  let dw, dh;
  if (winW / winH > imgRatio) {
    dw = winW;
    dh = winW / imgRatio;
  } else {
    dh = winH;
    dw = winH * imgRatio;
  }
  return {
    scale: dw / BG_W,
    originX: (winW - dw) / 2,
    originY: (winH - dh) / 2,
  };
};

/**
 * 汎用キャラクターコンポーネント
 *
 * - 1920×1024 内部座標で位置を指定する
 * - basePosition は「足元（接地点）」を表す
 * - parts が指定されていれば assetDir 配下のパーツ画像を読み込んで描画
 * - parts が無ければ placeholderColor の角丸矩形を描画（スケルトン）
 *
 * parts の各要素:
 *   {
 *     name:    'body',           // 識別子
 *     file:    'body.png',       // assetDir からの相対パス
 *     offsetX: -309,             // anchor からのソース画像左上 X オフセット (源画像pixel単位)
 *     offsetY: -938,             // 同 Y。負の値で「上」
 *     z:       3,                // 描画順（小→奥、大→手前）
 *     bob:     true,             // 頭を上下させる(任意)
 *     rotate:  { pivotX: 0, pivotY: -200, maxDeg: 15, speed: 1000 }, // 振り子(任意)
 *              + random: { minDelay, maxDelay, cycles, group, syncWith } を付けると常時ではなく
 *                ランダムな間隔で cycles 回だけ振る（cycles 1 = 上げて下ろす）。
 *                group を付けると同じ group のパーツは同時に発火しない（左右の腕の排他）。
 *                syncWith: '<パーツ名>' はそのパーツと同じタイミングで動く。
 *                delay: <ms> を足すと発火元より遅れて動き出す（肩→肘のタメ）。
 *                ease: true で「上げは素早く・下ろしはイージングアウト」の波形になる
 *                （riseRatio で上げ/下ろしの時間配分。既定 0.4）。
 *     parent:  'ude_r_1',        // 親パーツ名(任意)。親の回転を引き継ぐ（肩→肘の連動）
 *     sprite:  'eye.json',      // スプライトシートJSON（file の代わりに指定。同名 .png を読み込む）
 *   }
 *
 * bgPosition: { x, y } を渡すと背景 sofa.png(1920×2560) 内の座標で位置を指定する。
 *          背景と同じ cover 変換を使うため、ウィンドウ比率が変わっても背景に対する
 *          位置・大きさが固定される（カウンター奥に立たせるキャラ用）。
 * move:    横移動＋歩行の上下動。座標系は bgPosition 指定時は sofa.png 座標、それ以外は内部座標。
 *          { fromX, toX, duration, bobAmp, bobSpeed }  … 1区間をループ（従来形式）
 *          { bobAmp, bobSpeed, loop, steps: [...] }     … 複数ステップを順に再生
 *            steps の各要素:
 *              { fromX, toX, duration, y?, zIndex?, scaleMul?, flipX? } … 移動
 *              { hold: <ms>, x?, y?, zIndex?, hide? }           … その場で待機（bob 停止）
 *              fromX:'current' で「いまマスターがいる位置」から動き出す（fallbackX が既定値）
 *              speed（px/秒）を渡すと duration の代わりに距離から尺を計算する
 *              set:{ フラグ名: true/false } で共有フラグを操作（他プリセットの表示切替）
 *              hide: true のステップの間は非表示（プリセットの差し替えに使う）
 *            loop 未指定（steps 使用時）は false = 最後のステップの終点で停止
 *          { bobAmp, bobSpeed, wander: { minX, maxX, speed, minPause, maxPause, minStep, maxStep, startX, startAt } }
 *            … 範囲内をランダムに左右移動 → 停止 → 逆方向へ、を繰り返す。
 *              移動中は rotate の random 発火（仕草）を止めるので、止まってから仕草が出る。
 *              startAt / startWhen（共有フラグ名）でシーケンス開始まで非表示にできる。
 * sharedState: マスター3プリセットで共有する状態オブジェクト（位置と切替フラグ）
 * zIndex:  canvas の重ね順。既定は SCSS の 5。sofa(2) より奥に置きたいときは 1 を渡す。
 * blurred: true で背景と同じぼかしをかける（sofa 奥に置いたキャラ用）
 */
const Character = ({
  id,
  displayName,
  basePosition = { x: 960, y: 720 },
  scale = 0.5,
  flipX = false,
  placeholderColor = '#888',
  parts,
  assetDir,
  debug = false,
  isSpeaking = false,
  zIndex = null,
  blurred = false,
  bgPosition = null,
  move = null,
  timeOrigin = null,
  sharedState = null,
  paused = false,
}) => {
  const isSpeakingRef = useRef(isSpeaking);
  isSpeakingRef.current = isSpeaking;
  // 歩行シーケンスの起点時刻（null = まだ開始していない → move 指定のキャラは非表示）
  const timeOriginRef = useRef(timeOrigin);
  timeOriginRef.current = timeOrigin;
  // 一時停止中は時計を止める。止めないと再開時にマスターが先へ飛んでしまう
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const pauseStartRef = useRef(null);
  const pausedTotalRef = useRef(0);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // ---- パーツ画像のロード ----
    const partImages = {};
    // スプライトシート用: { [name]: { img, data } }
    const spriteSheets = {};
    let partsAllLoaded = false;
    if (parts && assetDir) {
      let loadedCount = 0;
      const totalLoads = parts.length;
      const checkDone = () => {
        loadedCount++;
        if (loadedCount === totalLoads) partsAllLoaded = true;
      };

      parts.forEach((p) => {
        if (p.sprite) {
          // スプライトシートパーツ: JSON を fetch → スプライトシート画像をロード
          fetch(`${assetDir}/${p.sprite}`)
            .then((res) => res.json())
            .then((data) => {
              const spriteImg = new Image();
              spriteImg.onload = () => {
                spriteSheets[p.name] = { img: spriteImg, data };
                checkDone();
              };
              spriteImg.onerror = () => {
                console.error(`[Character ${id}] failed to load sprite image for ${p.name}`);
                checkDone();
              };
              // JSON と同じベース名の .png を使う
              const pngFile = p.sprite.replace(/\.json$/, '.png');
              spriteImg.src = `${assetDir}/${pngFile}`;
            })
            .catch((err) => {
              console.error(`[Character ${id}] failed to load sprite JSON ${p.sprite}:`, err);
              checkDone();
            });
        } else {
          // 通常パーツ: 単一画像をロード
          const img = new Image();
          img.onload = () => {
            partImages[p.name] = img;
            checkDone();
          };
          img.onerror = () => {
            console.error(`[Character ${id}] failed to load ${assetDir}/${p.file}`);
            checkDone();
          };
          img.src = `${assetDir}/${p.file}`;
        }
      });
    }

    // ---- リサイズ ----
    const resizeCanvas = () => {
      // 実ピクセルは画面密度に合わせて確保する（スマホでの粗さ対策）。
      // 表示サイズは CSS で従来どおりに固定されるので、座標計算は変えなくてよい。
      // bgPosition 指定時は背景と同じ全面 canvas（cover 座標をそのまま使う）
      if (bgPosition) {
        sizeCanvas(canvas, window.innerWidth, window.innerHeight);
        return;
      }
      const windowRatio = window.innerWidth / window.innerHeight;
      if (windowRatio > TARGET_RATIO) {
        sizeCanvas(canvas, window.innerHeight * TARGET_RATIO, window.innerHeight);
      } else {
        sizeCanvas(canvas, window.innerWidth, window.innerWidth / TARGET_RATIO);
      }
    };

    // マスター3プリセットで共有する状態（現在位置・切替フラグ）
    const shared = sharedState ?? {};

    // ランダム発火アニメの状態（パーツ名 → { start }）
    const randomState = {};
    // 排他グループの占有状況（group名 → { owner, until }）
    const groupBusy = {};
    // wander（ランダム徘徊）の状態
    const wanderState = {};
    // steps（歩行シーケンス）の状態
    const stepState = {};

    // ---- パーツ描画 ----
    const drawParts = (timestamp) => {
      const { cssW: cw, cssH: ch } = cssSize(canvas);
      ctx.clearRect(0, 0, cw, ch);

      // アンカーとスケール
      // bgPosition 指定時は背景(sofa.png)座標系。ウィンドウ比率が変わっても
      // 背景に対する位置・大きさが変わらない（カウンター奥に固定したいキャラ用）
      let anchorX, anchorY, charScale, posScale, originX, originY;
      if (bgPosition) {
        const t = calcBgTransform();
        posScale = t.scale;
        originX = t.originX;
        originY = t.originY;
        anchorX = originX + bgPosition.x * posScale;
        anchorY = originY + bgPosition.y * posScale;
        charScale = posScale * scale;
      } else {
        posScale = cw / TARGET_W;
        originX = 0;
        originY = 0;
        charScale = posScale * scale;
        // 内部座標 → 実ピクセル（足元アンカー）
        anchorX = basePosition.x * posScale;
        anchorY = basePosition.y * (ch / TARGET_H);
      }

      // move: 横方向のスライド + 歩行の上下バウンド
      // 座標は bgPosition 使用時は背景 sofa.png 座標、それ以外は 1920×1024 内部座標
      let flipNow = flipX;
      // 移動中は仕草（random 発火の rotate）を出さない
      let gesturePaused = false;

      // move / wander はシーケンス起点からの経過時間で動かす
      // （起点が未設定＝会話開始前は、move 指定のキャラは描画しない）
      const seqTime = timeOriginRef.current == null ? null : timestamp - timeOriginRef.current;
      if (move && seqTime == null) return;

      // wander: 範囲内をランダムに左右移動 → 停止 → 逆方向へ、を繰り返す
      if (move && move.wander) {
        const w = move.wander;
        // startWhen: 共有フラグが立つまで非表示（他プリセットからの引き継ぎ用）
        if (w.startWhen && !shared[w.startWhen]) return;
        if (seqTime < (w.startAt ?? 0)) return; // 開始時刻まで非表示
        const rand = (min, max) => min + Math.random() * (max - min);
        const nextPause = () => seqTime + rand(w.minPause ?? 3000, w.maxPause ?? 8000);

        // シーケンスが再スタートしたら（seqTime が巻き戻ったら）状態を作り直す
        if (wanderState.lastSeq != null && seqTime < wanderState.lastSeq) {
          Object.keys(wanderState).forEach((k) => delete wanderState[k]);
        }
        wanderState.lastSeq = seqTime;

        if (!wanderState.phase) {
          wanderState.phase = 'pause';
          wanderState.x = w.startX ?? bgPosition?.x ?? basePosition.x;
          wanderState.until = nextPause();
          wanderState.dir = 1;
        }

        if (wanderState.phase === 'pause') {
          if (seqTime >= wanderState.until) {
            // 前回と逆方向へ。その方向に十分な距離が取れなければ向きを戻す
            let dir = -wanderState.dir;
            const minStep = w.minStep ?? 200;
            const roomAhead = dir > 0 ? w.maxX - wanderState.x : wanderState.x - w.minX;
            if (roomAhead < minStep) dir = -dir;
            const limit = dir > 0 ? w.maxX : w.minX;
            // 移動距離は minStep〜maxStep の範囲でランダム（端を超えないよう頭打ち）
            const room = Math.abs(limit - wanderState.x);
            const maxStep = Math.min(w.maxStep ?? room, room);
            const dist = rand(Math.min(minStep, maxStep), maxStep);
            wanderState.fromX = wanderState.x;
            wanderState.toX = wanderState.x + dir * dist;
            wanderState.dir = dir;
            wanderState.start = seqTime;
            wanderState.duration = (dist / (w.speed ?? 90)) * 1000;
            wanderState.phase = 'move';
          }
        } else {
          const p = (seqTime - wanderState.start) / wanderState.duration;
          if (p >= 1) {
            wanderState.x = wanderState.toX;
            wanderState.phase = 'pause';
            wanderState.until = nextPause();
          } else {
            // smoothstep で歩き出し／止まりを滑らかに
            const e = p * p * (3 - 2 * p);
            wanderState.x = wanderState.fromX + (wanderState.toX - wanderState.fromX) * e;
          }
        }

        anchorX = originX + wanderState.x * posScale;
        shared.x = wanderState.x; // 他プリセットが「その場から」続けられるよう共有
        if (wanderState.phase === 'move') {
          gesturePaused = true;
          if (move.bobAmp) {
            anchorY -=
              Math.abs(Math.sin(timestamp / (move.bobSpeed ?? 400))) * move.bobAmp * posScale;
          }
        }
      } else if (move) {
        // showWhen: 共有フラグが立っている間だけ表示（プリセットの差し替え用）
        if (move.showWhen && !shared[move.showWhen]) return;

        // steps 未指定なら「1ステップをループ」として扱う（従来の書き方と互換）
        const rawSteps = move.steps ?? [
          { fromX: move.fromX, toX: move.toX, duration: move.duration ?? 10000 },
        ];

        // シーケンスが再スタートしたら、開始位置を1回だけ確定し、立てたフラグを畳む
        if (stepState.lastSeq == null || seqTime < stepState.lastSeq) {
          stepState.startX = shared.x ?? null;
          rawSteps.forEach((st) => {
            if (st.set) Object.keys(st.set).forEach((k) => { shared[k] = false; });
          });
        }
        stepState.lastSeq = seqTime;

        // fromX:'current' と speed（px/秒）を解決してから尺を計算する
        const steps = rawSteps.map((st) => {
          const from = st.fromX === 'current' ? (stepState.startX ?? st.fallbackX ?? 0) : st.fromX;
          let len = st.duration ?? st.hold ?? 0;
          if (st.duration == null && st.speed != null && from != null && st.toX != null) {
            len = (Math.abs(st.toX - from) / st.speed) * 1000;
          }
          return { ...st, fromX: from, len };
        });

        const loop = move.loop ?? !move.steps;
        const total = steps.reduce((sum, st) => sum + st.len, 0);

        let t = seqTime;
        if (loop) {
          t %= total;
        } else if (t > total) {
          t = total; // ループしない場合は最後のステップの終点で停止
        }

        // 現在のステップと、その中での経過を求める。
        // 通過済みステップの最終状態（位置・大きさ・向き）は引き継ぐので、
        // 座標を書かない hold ステップは「その場に留まる」動きになる
        const carried = { x: null, y: null, scaleMul: null, flipX: null, zIndex: null };
        let acc = 0;
        let cur = steps[steps.length - 1];
        let local = 1; // 0..1
        for (let i = 0; i < steps.length; i++) {
          const st = steps[i];
          const len = st.len;
          if (t <= acc + len || i === steps.length - 1) {
            cur = st;
            local = len > 0 ? Math.min((t - acc) / len, 1) : 1;
            break;
          }
          if (st.fromX != null) carried.x = st.toX; // 移動ステップは終点で終わる
          if (st.x != null) carried.x = st.x;
          if (st.y != null) carried.y = st.y;
          if (st.scaleMul != null) carried.scaleMul = st.scaleMul;
          if (st.flipX != null) carried.flipX = st.flipX;
          if (st.zIndex != null) carried.zIndex = st.zIndex;
          acc += len;
        }

        const moving = cur.hold == null && cur.fromX != null;
        if (moving) {
          const x = cur.fromX + (cur.toX - cur.fromX) * local;
          anchorX = originX + x * posScale;
        } else {
          const x = cur.x ?? carried.x;
          if (x != null) anchorX = originX + x * posScale;
        }
        const curY = cur.y ?? carried.y;
        if (curY != null) {
          anchorY = originY + curY * posScale;
        }
        const curScaleMul = cur.scaleMul ?? carried.scaleMul;
        if (curScaleMul) {
          charScale *= curScaleMul;
        }
        const curZIndex = cur.zIndex ?? carried.zIndex;
        const curFlipX = cur.flipX ?? carried.flipX;
        // 歩いている間だけ上下に弾む（停止・待機中は止まる）
        if (moving && move.bobAmp) {
          anchorY -= Math.abs(Math.sin(timestamp / (move.bobSpeed ?? 400))) * move.bobAmp * posScale;
        }
        // ステップが持つ共有フラグを立てる／畳む（他プリセットの表示切替に使う）
        if (cur.set) Object.assign(shared, cur.set);
        if (moving) shared.x = cur.fromX + (cur.toX - cur.fromX) * local;

        // hide: true のステップの間は描画しない（プリセットの切り替え用）
        if (cur.hide) return;
        // ステップごとに左右反転を切り替える（進行方向に向ける）
        if (curFlipX != null) flipNow = curFlipX;
        // ステップごとに重ね順を切り替える（カウンター奥 → 画面手前 など）
        if (curZIndex != null && canvas.style.zIndex !== String(curZIndex)) {
          canvas.style.zIndex = String(curZIndex);
        }
      }

      // z 昇順（奥→手前）でソート
      const sortedParts = [...parts].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));

      // parent 参照用（name → パーツ定義）
      const partByName = {};
      parts.forEach((p) => { partByName[p.name] = p; });

      // 頭部の上下バウンド（共通の波形）
      // bob: true で振幅 8（ソース画像px）、数値を渡すとその値を振幅にする
      const bobWave = Math.sin(timestamp / 600);
      const bobOf = (p) => bobWave * (typeof p.bob === 'number' ? p.bob : 8) * charScale;

      // このフレームの各パーツの回転角を先に確定させる（親子で同じ値を使うため）
      const angles = {};
      const durationOf = (r) =>
        (r.random?.cycles ?? 1) * Math.PI * (r.speed ?? 1000); // 半周期 × cycles
      const syncedParts = [];

      // 発火中の波形（0→1→0）。ease 指定時は「上げは素早く・下ろしはイージングアウト」
      const waveOf = (r, elapsed) => {
        const speed = r.speed ?? 1000;
        if (!r.random?.ease) return Math.sin(elapsed / speed);

        const p = elapsed / durationOf(r); // 0..1
        const riseRatio = r.random.riseRatio ?? 0.4; // 上げに使う割合（残りが下ろし）
        if (p < riseRatio) {
          // 上げ: 従来と同じ立ち上がり
          return Math.sin((p / riseRatio) * (Math.PI / 2));
        }
        // 下ろし: 終端の速度が 0 になるコサインイージング（ゆっくり収まる）
        const t = (p - riseRatio) / (1 - riseRatio);
        return (1 + Math.cos(Math.PI * t)) / 2;
      };

      parts.forEach((p) => {
        if (!p.rotate) return;
        const r = p.rotate;
        const maxAngle = ((r.maxDeg ?? 15) * Math.PI) / 180;
        const speed = r.speed ?? 1000;

        // random 未指定なら従来どおり常時ゆれ続ける
        if (!r.random) {
          angles[p.name] = Math.sin(timestamp / speed) * maxAngle;
          return;
        }

        // 他パーツと同じタイミングで動くものは後回し（発火元の状態が要るため）
        if (r.random.syncWith) {
          syncedParts.push(p);
          return;
        }

        // random: { minDelay, maxDelay, cycles, group }
        // 休止 → cycles 回だけ振る → 休止… を繰り返す。cycles 1 = 片道往復（上げて下ろす）
        // group を指定すると、同じ group のパーツは同時に動かない（排他）
        const { minDelay = 2000, maxDelay = 6000, group = null } = r.random;
        const nextDelay = () => minDelay + Math.random() * (maxDelay - minDelay);
        const duration = durationOf(r);

        let st = randomState[p.name];
        if (!st) {
          st = randomState[p.name] = { start: timestamp + nextDelay() };
        }

        if (timestamp < st.start) {
          angles[p.name] = 0;
          return;
        }

        // 移動中は新しい仕草を始めない（発火を先送り）
        if (gesturePaused) {
          st.start = timestamp + nextDelay();
          angles[p.name] = 0;
          return;
        }

        // グループが他のパーツで塞がっていたら、空くまで発火を先送り
        if (group) {
          const busy = groupBusy[group];
          if (busy && busy.owner !== p.name && busy.until > timestamp) {
            st.start = busy.until + nextDelay();
            angles[p.name] = 0;
            return;
          }
        }

        const elapsed = timestamp - st.start;
        if (elapsed >= duration) {
          st.start = timestamp + nextDelay();
          angles[p.name] = 0;
          return;
        }

        if (group) {
          groupBusy[group] = { owner: p.name, until: st.start + duration };
        }
        angles[p.name] = waveOf(r, elapsed) * maxAngle;
      });

      // syncWith 指定のパーツ: 発火元と同じ start を使う（腕1本を1トリガーで動かす用）
      // delay を指定すると発火元より遅れて動き出す（肩→肘のタメ）
      syncedParts.forEach((p) => {
        const r = p.rotate;
        const maxAngle = ((r.maxDeg ?? 15) * Math.PI) / 180;
        const src = randomState[r.random.syncWith];
        const elapsed = src ? timestamp - (src.start + (r.random.delay ?? 0)) : -1;
        angles[p.name] =
          elapsed >= 0 && elapsed < durationOf(r) ? waveOf(r, elapsed) * maxAngle : 0;
      });

      // rotate を ctx に適用（pivot 中心の回転）
      const applyRotate = (p) => {
        const r = p.rotate;
        const pivotX = anchorX + (r.pivotX ?? 0) * charScale;
        const pivotY = anchorY + (r.pivotY ?? 0) * charScale;
        ctx.translate(pivotX, pivotY);
        ctx.rotate(angles[p.name] ?? 0);
        ctx.translate(-pivotX, -pivotY);
      };

      // parent 指定の親パーツを根元から順に並べる（肩→肘のように回転を継承させる）
      const ancestorsOf = (p) => {
        const chain = [];
        let cur = p.parent ? partByName[p.parent] : null;
        let guard = 0;
        while (cur && guard++ < 8) {
          chain.unshift(cur);
          cur = cur.parent ? partByName[cur.parent] : null;
        }
        return chain;
      };

      ctx.save();
      if (flipNow) {
        ctx.translate(anchorX, 0);
        ctx.scale(-1, 1);
        ctx.translate(-anchorX, 0);
      }

      sortedParts.forEach((p) => {
        // ---- スプライトシートパーツ ----
        const ss = spriteSheets[p.name];
        if (ss) {
          const { img: spriteImg, data: spriteData } = ss;
          if (!spriteImg || !spriteImg.complete || spriteImg.naturalWidth === 0) return;

          let frameIndex;
          const isMouth = p.name === 'mouth';

          if (isMouth && !isSpeakingRef.current) {
            // 喋っていないときは口を閉じる（フレーム0）
            frameIndex = 0;
          } else {
            // フレームインデックス計算（再生 + 2秒待機のループ）
            const cycleDuration = (spriteData.frames.length / spriteData.fps_hint) * 1000;
            const waitDuration = isMouth ? 0 : 2000; // 口は喋り中は休みなし
            const totalDuration = cycleDuration + waitDuration;
            const loopTime = timestamp % totalDuration;

            if (loopTime < cycleDuration) {
              frameIndex = Math.floor((loopTime / cycleDuration) * spriteData.frames.length);
            } else {
              frameIndex = 0;
            }
          }

          const frame = spriteData.frames[frameIndex];
          const w = frame.w * charScale;
          const h = frame.h * charScale;
          let dx = anchorX + (p.offsetX ?? 0) * charScale;
          let dy = anchorY + (p.offsetY ?? 0) * charScale;
          if (p.bob) dy += bobOf(p);

          ctx.drawImage(
            spriteImg,
            frame.x, frame.y, frame.w, frame.h, // ソース（スプライトシート内の切り抜き）
            dx, dy, w, h                          // 描画先
          );
          return;
        }

        // ---- 通常パーツ ----
        const img = partImages[p.name];
        if (!img || !img.complete || img.naturalWidth === 0) return;

        const w = img.width * charScale;
        const h = img.height * charScale;
        let dx = anchorX + (p.offsetX ?? 0) * charScale;
        let dy = anchorY + (p.offsetY ?? 0) * charScale;

        if (p.bob) dy += bobOf(p);

        const chain = ancestorsOf(p);
        if (chain.length > 0 || p.rotate) {
          ctx.save();
          // 親（根元→末端）の回転を先に適用してから自分の回転
          chain.forEach((a) => {
            if (a.rotate) applyRotate(a);
          });
          if (p.rotate) applyRotate(p);
          ctx.drawImage(img, dx, dy, w, h);
          ctx.restore();
        } else {
          ctx.drawImage(img, dx, dy, w, h);
        }
      });

      ctx.restore();

      // ---- debug 表示 ----
      if (debug) {
        // アンカー位置の十字
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.9)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(anchorX - 24, anchorY);
        ctx.lineTo(anchorX + 24, anchorY);
        ctx.moveTo(anchorX, anchorY - 24);
        ctx.lineTo(anchorX, anchorY + 24);
        ctx.stroke();

        // 各パーツの bbox
        sortedParts.forEach((p) => {
          let w, h;
          const ss = spriteSheets[p.name];
          if (ss) {
            w = ss.data.frame_w * charScale;
            h = ss.data.frame_h * charScale;
          } else {
            const img = partImages[p.name];
            if (!img || !img.complete || img.naturalWidth === 0) return;
            w = img.width * charScale;
            h = img.height * charScale;
          }
          const dx = anchorX + (p.offsetX ?? 0) * charScale;
          const dy = anchorY + (p.offsetY ?? 0) * charScale;
          ctx.strokeStyle = ss ? 'rgba(0, 255, 100, 0.6)' : 'rgba(0, 200, 255, 0.6)';
          ctx.lineWidth = 1;
          ctx.strokeRect(dx, dy, w, h);
          ctx.fillStyle = ss ? 'rgba(0, 255, 100, 0.9)' : 'rgba(0, 200, 255, 0.9)';
          ctx.font = `${12}px monospace`;
          ctx.fillText(p.name + (ss ? ' [sprite]' : ''), dx + 4, dy + 14);
        });

        // 回転 pivot を黄色マーカーで可視化
        sortedParts.forEach((p) => {
          if (!p.rotate) return;
          const pivotX = anchorX + (p.rotate.pivotX ?? 0) * charScale;
          const pivotY = anchorY + (p.rotate.pivotY ?? 0) * charScale;

          // 中心円（塗り）
          ctx.fillStyle = 'rgba(255, 220, 0, 0.95)';
          ctx.beginPath();
          ctx.arc(pivotX, pivotY, 6, 0, Math.PI * 2);
          ctx.fill();

          // 外周枠
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
          ctx.lineWidth = 1.5;
          ctx.stroke();

          // 十字
          ctx.strokeStyle = 'rgba(255, 220, 0, 0.95)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(pivotX - 14, pivotY);
          ctx.lineTo(pivotX + 14, pivotY);
          ctx.moveTo(pivotX, pivotY - 14);
          ctx.lineTo(pivotX, pivotY + 14);
          ctx.stroke();

          // 可動範囲の弧
          const maxAngle = ((p.rotate.maxDeg ?? 15) * Math.PI) / 180;
          const radius = 60;
          ctx.strokeStyle = 'rgba(255, 220, 0, 0.5)';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(pivotX, pivotY, radius, Math.PI / 2 - maxAngle, Math.PI / 2 + maxAngle);
          ctx.stroke();

          // ラベル
          ctx.fillStyle = 'rgba(255, 220, 0, 0.95)';
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
          ctx.lineWidth = 3;
          ctx.font = 'bold 11px monospace';
          const label = `${p.name} pivot (${p.rotate.pivotX ?? 0}, ${p.rotate.pivotY ?? 0})`;
          ctx.strokeText(label, pivotX + 12, pivotY - 10);
          ctx.fillText(label, pivotX + 12, pivotY - 10);
        });

        // ラベル
        ctx.fillStyle = 'rgba(255, 0, 0, 0.95)';
        ctx.font = 'bold 14px monospace';
        ctx.fillText(`${id} (${basePosition.x}, ${basePosition.y})`, anchorX + 28, anchorY + 4);
      }
    };

    // ---- スケルトン用プレースホルダ描画 ----
    const drawPlaceholder = (timestamp = 0) => {
      const { cssW: cw, cssH: ch } = cssSize(canvas);
      ctx.clearRect(0, 0, cw, ch);

      const baseScale = cw / TARGET_W;
      const cx = basePosition.x * baseScale;
      const cy = basePosition.y * (ch / TARGET_H);

      const phase = id ? id.charCodeAt(0) : 0;
      const bounceY = Math.sin(timestamp / 600 + phase) * 8 * baseScale;

      const figW = 280 * baseScale * (scale * 2);
      const figH = 520 * baseScale * (scale * 2);
      const x = cx - figW / 2;
      const y = cy - figH + bounceY;

      ctx.save();
      if (flipX) {
        ctx.translate(cx, 0);
        ctx.scale(-1, 1);
        ctx.translate(-cx, 0);
      }

      // 影
      ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
      ctx.beginPath();
      ctx.ellipse(cx, cy + 4 * baseScale, figW * 0.45, 14 * baseScale, 0, 0, Math.PI * 2);
      ctx.fill();

      // 体
      ctx.fillStyle = placeholderColor;
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
      ctx.lineWidth = 3 * baseScale;
      const r = 24 * baseScale;
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + figW - r, y);
      ctx.quadraticCurveTo(x + figW, y, x + figW, y + r);
      ctx.lineTo(x + figW, y + figH - r);
      ctx.quadraticCurveTo(x + figW, y + figH, x + figW - r, y + figH);
      ctx.lineTo(x + r, y + figH);
      ctx.quadraticCurveTo(x, y + figH, x, y + figH - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // 顔
      const faceR = figW * 0.34;
      const faceCx = cx;
      const faceCy = y + faceR + 18 * baseScale;
      ctx.fillStyle = '#f6e1c7';
      ctx.beginPath();
      ctx.arc(faceCx, faceCy, faceR, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // 名前
      if (displayName) {
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.65)';
        ctx.lineWidth = 4 * baseScale;
        ctx.font = `bold ${28 * baseScale}px "Helvetica Neue", Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const labelY = y + figH * 0.62;
        ctx.strokeText(displayName, cx, labelY);
        ctx.fillText(displayName, cx, labelY);
      }

      ctx.restore();
    };

    // ---- メインループ ----
    const draw = (timestamp) => {
      if (parts && assetDir) {
        // パーツ定義あり → 読み込み完了後のみ描画（読み込み中は空キャンバス）
        if (partsAllLoaded) drawParts(timestamp);
        else { const { cssW, cssH } = cssSize(canvas); ctx.clearRect(0, 0, cssW, cssH); }
      } else {
        drawPlaceholder(timestamp);
      }
    };

    const animate = (timestamp) => {
      // 一時停止中は時刻を進めない。再開したら止まっていた分を差し引いて続きから動かす
      if (pausedRef.current) {
        if (pauseStartRef.current == null) pauseStartRef.current = timestamp;
      } else if (pauseStartRef.current != null) {
        pausedTotalRef.current += timestamp - pauseStartRef.current;
        pauseStartRef.current = null;
      }
      const frozen = pauseStartRef.current ?? timestamp;
      draw(frozen - pausedTotalRef.current);
      animationRef.current = requestAnimationFrame(animate);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [
    id,
    displayName,
    basePosition.x,
    basePosition.y,
    scale,
    flipX,
    placeholderColor,
    parts,
    assetDir,
    debug,
    bgPosition?.x,
    bgPosition?.y,
    move,
  ]);

  return (
    <canvas
      ref={canvasRef}
      className={`character-canvas${blurred ? ' blurred' : ''}`}
      data-character-id={id}
      style={zIndex != null ? { zIndex } : undefined}
    />
  );
};

export default Character;
